import React from 'react';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import { TextSize } from '../../utils/textSize';

interface TextSizeControlProps {
  value: TextSize;
  onChange: (size: TextSize) => void;
  /** Use on dark AppBar backgrounds */
  invertColors?: boolean;
  /** Hide the label row (header toolbar) */
  compact?: boolean;
  testId?: string;
}

const OPTIONS: { value: TextSize; label: string; shortLabel: string }[] = [
  { value: 'small', label: 'Small text', shortLabel: 'A' },
  { value: 'default', label: 'Default text', shortLabel: 'A' },
  { value: 'large', label: 'Large text', shortLabel: 'A' },
];

export const TextSizeControl: React.FC<TextSizeControlProps> = ({
  value,
  onChange,
  invertColors = false,
  compact = false,
  testId = 'text-size-control',
}) => {
  const handleChange = (_event: React.MouseEvent<HTMLElement>, newValue: TextSize | null) => {
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  return (
    <Box
      data-testid={testId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {!compact && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
          <FormatSizeIcon
            fontSize="small"
            sx={{ color: invertColors ? 'inherit' : 'text.secondary' }}
            aria-hidden
          />
          <Typography
            variant="body2"
            component="span"
            sx={{
              display: { xs: 'none', sm: 'inline' },
              color: invertColors ? 'inherit' : 'text.secondary',
            }}
          >
            Text size
          </Typography>
        </Box>
      )}
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={handleChange}
        aria-label="Text size"
        size="small"
        sx={
          invertColors
            ? {
                '& .MuiToggleButton-root': {
                  color: 'inherit',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&.Mui-selected': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'inherit',
                  },
                },
              }
            : undefined
        }
      >
        {OPTIONS.map((option, index) => (
          <Tooltip key={option.value} title={option.label}>
            <ToggleButton
              value={option.value}
              aria-label={option.label}
              data-testid={`text-size-${option.value}`}
              sx={{
                minWidth: 36,
                px: 1,
                fontSize: index === 0 ? '0.75rem' : index === 1 ? '0.875rem' : '1rem',
                fontWeight: value === option.value ? 700 : 400,
              }}
            >
              {option.shortLabel}
            </ToggleButton>
          </Tooltip>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};
